import { AlertColor } from "@mui/material";

import { setMessage } from "../redux/notification";

/**
 * Utility function to show a notification message
 * @param content The message content to display
 * @param type The type of notification (error, warning, info, success)
 * @param dispatch The Redux dispatch function
 * @param isLoading Whether the notification is in a loading state
 */
export const showNotification = (
  content: string,
  type: AlertColor = "info",
  dispatch: any,
  isLoading = false
) => {
  dispatch(setMessage({ content, type, isLoading }));
};

/**
 * Utility function to show an error notification
 * @param content The error message to display
 * @param dispatch The Redux dispatch function
 */
export const showError = (content: string, dispatch: any) => {
  showNotification(content, "error", dispatch);
};

/**
 * Utility function to show a success notification
 * @param content The success message to display
 * @param dispatch The Redux dispatch function
 */
export const showSuccess = (content: string, dispatch: any) => {
  showNotification(content, "success", dispatch);
};

/**
 * Utility function to show a warning notification
 * @param content The warning message to display
 * @param dispatch The Redux dispatch function
 */
export const showWarning = (content: string, dispatch: any) => {
  showNotification(content, "warning", dispatch);
};

/**
 * Utility function to show an info notification
 * @param content The info message to display
 * @param dispatch The Redux dispatch function
 */
export const showInfo = (content: string, dispatch: any) => {
  showNotification(content, "info", dispatch);
};

/**
 * Utility function to show a loading notification
 * @param content The loading message to display
 * @param dispatch The Redux dispatch function
 */
export const showLoading = (content: string, dispatch: any) => {
  showNotification(content, "info", dispatch, true);
};

/**
 * Utility function to clear any active notification
 * @param dispatch The Redux dispatch function
 */
export const clearNotification = (dispatch: any) => {
  dispatch(setMessage(null));
};

/**
 * Convert a technical error into a short, user-friendly message.
 *
 *
 * Current mappings (examples):
 * - network -> "Network error. Please check your connection and try again."
 * - failed to fetch -> "Unable to load the requested information. Please try again."
 * - size / file too large / payload -> "The selected file is too large. Please try a smaller file."
 * - permission / unauthorized / 403 -> "You don't have permission to perform this action. Please try signing in again or contact support."
 * - other errors -> "Something went wrong. Please try again."
 *
 * @param err - An Error or other thrown value
 * @returns short user-friendly message
 */
export const getFriendlyErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes("network")) {
      return "Network error. Please check your connection and try again.";
    }
    if (msg.includes("failed to fetch")) {
      return "Unable to load the requested information. Please try again.";
    }
    if (
      msg.includes("size") ||
      msg.includes("file too large") ||
      msg.includes("payload")
    ) {
      return "The selected file is too large. Please try a smaller file.";
    }
    if (
      msg.includes("permission") ||
      msg.includes("unauthorized") ||
      msg.includes("403")
    ) {
      return "You don't have permission to perform this action. Please try signing in again or contact support.";
    }
    // Generic fallback
    return "Something went wrong. Please try again.";
  }
  return "An unexpected error occurred. Please try again.";
};

/**
 * Log a technical error and show a short, user-friendly toast.
 *
 * Usage patterns:
 * - notifyError(err, dispatch)
 *     Logs `err` and shows a friendly mapped message.
 * - notifyError(err, dispatch, "Custom message")
 *     Logs `err` and shows the provided custom message instead of the mapped one.
 * - notifyError(undefined, dispatch, "Custom message only")
 *     Shows the provided message without logging an error.
 *
 *
 * @param err - The thrown error (optional if customMessage is provided)
 * @param dispatch - Redux dispatch function
 * @param customMessage - Override the computed friendly message
 */
export const notifyError = (err: unknown, dispatch: any, customMessage?: string) => {
  // Preserve full technical error in console for debugging
  if (err !== undefined) console.error(err);

  const message = customMessage || getFriendlyErrorMessage(err);
  showError(message, dispatch);
};
